// Web Push in a browser — the ONE implementation every console uses (the mailbox's, proven in
// production, moved here so the admin console reuses it instead of growing a second copy).
//
// The part worth care is the STATE MACHINE, because there are four different reasons push can be
// off and only one of them is "you have not turned it on":
//
//   * the browser does not support it at all
//   * permission was denied — and a denied permission cannot be re-asked by a button; the user has
//     to change it in the browser's own site settings, so saying "click to enable" is a lie
//   * permission is granted but this device is not subscribed
//   * subscribed here, and possibly on other devices too

export type BrowserPushState = "unsupported" | "denied" | "prompt" | "granted";

export function browserPushState(): BrowserPushState {
  if (typeof window === "undefined") return "unsupported";
  if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
    return "unsupported";
  }
  const p = Notification.permission;
  if (p === "denied") return "denied";
  if (p === "granted") return "granted";
  return "prompt";
}

// The VAPID key arrives base64url and the browser wants raw bytes. Getting this wrong fails at
// subscribe() with an opaque error, which is why it is a named function rather than inline.
export function urlBase64ToArrayBuffer(base64: string): ArrayBuffer {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  // The ArrayBuffer rather than the view: applicationServerKey's type excludes a Uint8Array whose
  // buffer could be a SharedArrayBuffer, and the buffer itself is what the API actually wants.
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out.buffer;
}

export interface SubscribeBrowserOptions {
  /** Absolute URL of the console's own base path: its sw.js is there and its scope is that path.
   * Three consoles share one origin, and an absolute "/sw.js" would have them fighting over one
   * registration. */
  baseURL: string;
  /** The server's applicationServerKey (base64url). */
  vapidKey: () => Promise<string>;
  /** Stores the subscription server-side for the caller's own principal. */
  save: (endpoint: string, keys: unknown, userAgent: string) => Promise<void>;
}

/**
 * Asks for permission, registers the console's service worker, subscribes with the server's VAPID
 * key and stores the subscription. Resolves to the permission state reached — anything but
 * "granted" means nothing was subscribed, and the caller says why. Throws, with a reason a person
 * can act on, when permission was granted and a later step failed.
 */
export async function subscribeThisBrowser(opts: SubscribeBrowserOptions): Promise<BrowserPushState> {
  if (browserPushState() === "unsupported") return "unsupported";
  // Asked only when not already granted: asking again is harmless on most browsers, but the
  // migration below runs without a tap and must never raise a prompt.
  const perm = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
  if (perm !== "granted") return perm === "denied" ? "denied" : "prompt";
  const key = await opts.vapidKey();
  if (!key) {
    // Say which half is missing. Without this the failure surfaces as a TypeError from the base64
    // conversion, which names nothing a person can act on.
    throw new Error("this server did not return a push key, so this browser cannot subscribe");
  }
  // A registration failure is the error it is — an earlier version fell through to
  // serviceWorker.ready behind a .catch, so a missing worker looked like a hang.
  const registration = await navigator.serviceWorker.register(new URL("sw.js", opts.baseURL).toString(), {
    scope: opts.baseURL,
  });
  await navigator.serviceWorker.ready;
  const sub = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToArrayBuffer(key),
  });
  const j = sub.toJSON() as { endpoint?: string; keys?: unknown };
  await opts.save(j.endpoint ?? sub.endpoint, j.keys, navigator.userAgent);
  return "granted";
}

/** This browser's own subscription under the console's worker, or null. Never prompts. */
export async function thisBrowserSubscription(baseURL: string): Promise<PushSubscription | null> {
  if (browserPushState() === "unsupported") return null;
  const reg = await navigator.serviceWorker.getRegistration(baseURL);
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

/**
 * Stops pushes to THIS browser. The browser's subscription is ended at once; the server's row for
 * it is removed by the server itself the next time it sends there and the push service answers
 * "gone" (internal/modules/notify/send.go prunes 404/410). The caller may also delete the row when
 * it knows which one it is.
 */
export async function unsubscribeThisBrowser(baseURL: string): Promise<boolean> {
  const sub = await thisBrowserSubscription(baseURL);
  if (!sub) return false;
  return sub.unsubscribe();
}

/** The legacy, origin-wide worker the v1 mailbox page registered (served by the webmail hub as /sw.js). */
export const LEGACY_PUSH_WORKER_PATH = "/sw.js";

export type MigrationResult =
  | { kind: "none" }
  | { kind: "moved" }
  | { kind: "cleaned" }
  | { kind: "kept"; reason: string };

/**
 * Moves a push subscription held by the LEGACY worker onto the console's own worker — once, on
 * the next open, with no tap and no prompt.
 *
 * Why it exists: there were two workers for one app. The v1 mailbox page (and the v2 console when
 * opened at /app, which registered "./sw.js" relative to the page) put a worker at the ORIGIN root,
 * /sw.js with scope "/"; the console at /ozpanel/ registers /ozpanel/sw.js. A person could hold
 * their only subscription on the old one. From now on there is one worker (the console's), and
 * this hands the old subscription over instead of dropping it:
 *
 *   1. Find a registration whose script is exactly /sw.js — the legacy worker and nothing else.
 *      The other consoles' workers on the same origin (/admin/, /account/) are never touched.
 *   2. If it holds a subscription and notifications are allowed, subscribe the console's worker
 *      (same VAPID key, no prompt because permission is already granted) and SAVE that to the
 *      server.
 *   3. Only after the save succeeded: end the old subscription and unregister the old worker. The
 *      server's row for the old endpoint is pruned by the server on its next send (404/410).
 *
 * Anything that fails leaves the old worker and its subscription exactly as they were, so pushes
 * keep arriving there, and the result says why. Never a silent drop.
 */
export async function migrateLegacyPushRegistrations(opts: SubscribeBrowserOptions): Promise<MigrationResult> {
  if (browserPushState() === "unsupported") return { kind: "none" };
  const ours = new URL(opts.baseURL).href;
  const regs = await navigator.serviceWorker.getRegistrations();
  const legacy = regs.filter((r) => {
    if (r.scope === ours) return false;
    const w = r.active ?? r.waiting ?? r.installing;
    if (!w) return false;
    try {
      const u = new URL(w.scriptURL);
      return u.origin === location.origin && u.pathname === LEGACY_PUSH_WORKER_PATH;
    } catch {
      return false;
    }
  });
  if (legacy.length === 0) return { kind: "none" };

  let moved = false;
  for (const reg of legacy) {
    const old = await reg.pushManager.getSubscription();
    if (old) {
      if (Notification.permission !== "granted") {
        return { kind: "kept", reason: "notifications are not allowed in this browser any more" };
      }
      if (!moved) {
        try {
          const reached = await subscribeThisBrowser(opts);
          if (reached !== "granted") return { kind: "kept", reason: "this browser did not allow a new subscription" };
          moved = true;
        } catch (e) {
          return { kind: "kept", reason: e instanceof Error ? e.message : String(e) };
        }
      }
      await old.unsubscribe().catch(() => false);
    }
    await reg.unregister().catch(() => false);
  }
  return moved ? { kind: "moved" } : { kind: "cleaned" };
}
